import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Dashboard,
  Business,
  Receipt,
  Description,
  ExitToApp,
  Settings,
  Notifications,
  AccountBalance,
  Archive,
  Assessment,
  LocalAtm,
  CreditCard,
  Star,
  Group,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import api from '../../services/api';
import { loadCompanyProfile, saveCompanyProfile } from '../../utils/companyProfileStorage';
import { loadActivityMode } from '../../utils/activityModeStorage';
import facteioLogo from '../../assets/Facteio nouveau Logo.png';
import AppFooter from '../AppFooter';
import { useLanguage, languages } from '../../context/LanguageContext';
import { t } from '../../utils/translations';

const drawerWidth = 280;

const ModernAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #5E564F 0%, #746960 52%, #4F4741 100%)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0px 4px 20px rgba(79, 71, 65, 0.28)',
  overflow: 'visible',
  zIndex: theme.zIndex.drawer + 1,
  transition: 'none', // Désactiver les transitions pour éviter le clignotement
}));

const ModernDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    background: 'linear-gradient(135deg, #F4F0EB 0%, #E8E1D9 100%)',
    backdropFilter: 'blur(20px)',
    border: 'none',
    boxShadow: '0px 0px 40px rgba(31, 41, 55, 0.12)',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
    // Décaler le drawer sous l'AppBar (doit matcher la hauteur réelle du Toolbar)
    top: '64px',
    height: 'calc(100vh - 64px)',
    zIndex: theme.zIndex.drawer, // S'assurer que le zIndex est correct

    [theme.breakpoints.up('md')]: {
      top: '68px',
      height: 'calc(100vh - 68px)',
    },
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(139, 129, 120, 0.12) 0%, transparent 50%, rgba(197, 164, 109, 0.07) 100%)',
      opacity: 0.3,
    },
  },
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  background: 'linear-gradient(135deg, #EDE6DE 0%, #F5F0EA 100%)',
  color: '#5E564F',
  position: 'relative',
  overflow: 'visible',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
    borderRadius: '50%',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -30,
    left: -30,
    width: 80,
    height: 80,
    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
    borderRadius: '50%',
  },
}));

const ModernListItemButton = styled(ListItemButton)(({ theme, selected }) => ({
  borderRadius: 16,
  margin: '4px 12px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(197, 164, 109, 0.16) 0%, rgba(107, 98, 91, 0.08) 100%)',
    opacity: selected ? 1 : 0,
    transition: 'opacity 0.3s ease',
    borderRadius: 16,
  },
  
  '&:hover': {
    backgroundColor: 'rgba(139, 129, 120, 0.22)',
    transform: 'translateX(8px)',
    boxShadow: '0px 8px 25px rgba(79, 71, 65, 0.12)',
    
    '&::before': {
      opacity: 1,
    },
    
    '& .MuiListItemIcon-root': {
      transform: 'scale(1.1)',
    },
  },
  
  ...(selected && {
    backgroundColor: 'rgba(197, 164, 109, 0.2)',
    boxShadow: '0px 8px 25px rgba(79, 71, 65, 0.14)',
    
    '& .MuiListItemIcon-root': {
      color: '#5E564F',
      filter: 'drop-shadow(0px 0px 6px rgba(79, 71, 65, 0.2))',
    },
    
    '& .MuiListItemText-primary': {
      color: '#5E564F',
      fontWeight: 700,
    },
  }),
}));

const GlowingAvatar = styled(Avatar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #746960 0%, #5E564F 100%)',
  boxShadow: '0px 4px 20px rgba(79, 71, 65, 0.3)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0px 8px 30px rgba(79, 71, 65, 0.45)',
  },
}));

const normalizeSubscriptionValue = (value) => (value || '').toString().trim().toLowerCase();

const Layout = ({ children, onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [companyProfile, setCompanyProfile] = React.useState(null);
  const [subscription, setSubscription] = useState(null);
  const [storedUserSubscription, setStoredUserSubscription] = useState(() => {
    try {
      const rawUser = localStorage.getItem('user');
      return rawUser ? JSON.parse(rawUser) : null;
    } catch (error) {
      return null;
    }
  });

  const [activityMode, setActivityMode] = useState(() => loadActivityMode());

  const userCompanyName =
    storedUserSubscription?.companyName ||
    storedUserSubscription?.company?.name ||
    '';

  const legacyCompanyProfile = loadCompanyProfile();

  const displayCompanyName =
    companyProfile?.name ||
    userCompanyName ||
    legacyCompanyProfile?.name ||
    'Mon entreprise';

  const displayCompanyLogo =
    companyProfile?.logo ||
    storedUserSubscription?.logo ||
    legacyCompanyProfile?.logo ||
    '';

  const subscriptionPlan = normalizeSubscriptionValue(
    subscription?.subscriptionPlan || storedUserSubscription?.subscriptionPlan
  );
  const isAdmin = storedUserSubscription?.role === 'ADMIN';
  const isProPlan = isAdmin || subscriptionPlan === 'pro' || subscriptionPlan === 'enterprise';

  // Charger le statut d'abonnement
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get('/subscription/status');
        setSubscription(response.data);
      } catch (error) {
        console.error('Erreur chargement subscription:', error);
      }
    };
    fetchSubscription();
  }, []);

  useEffect(() => {
    const refresh = () => setActivityMode(loadActivityMode());
    window.addEventListener('activityModeUpdated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('activityModeUpdated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // Charger le profil de l'entreprise
  React.useEffect(() => {
    const syncFromStorage = () => {
      const storedProfile = loadCompanyProfile();
      if (storedProfile) {
        setCompanyProfile(storedProfile);
      }

      try {
        const rawUser = localStorage.getItem('user');
        setStoredUserSubscription(rawUser ? JSON.parse(rawUser) : null);
      } catch (error) {
        setStoredUserSubscription(null);
      }
    };

    const fetchProfile = async () => {
      try {
        const response = await api.get('/auth/profile');
        const userData = response?.data?.user;
        if (userData) {
          const profileFromApi = {};
          if (userData.companyName) profileFromApi.name = userData.companyName;
          if (userData.logo) profileFromApi.logo = userData.logo;
          if (userData.address) profileFromApi.address = userData.address;
          if (userData.postalCode) profileFromApi.postalCode = userData.postalCode;
          if (userData.city) profileFromApi.city = userData.city;
          if (userData.country) profileFromApi.country = userData.country;
          if (userData.phone) profileFromApi.phone = userData.phone;
          if (userData.siret) profileFromApi.siret = userData.siret;
          if (userData.vatNumber) profileFromApi.vatNumber = userData.vatNumber;
          if (userData.vatLiable !== undefined && userData.vatLiable !== null) profileFromApi.vatLiable = userData.vatLiable;
          if (userData.defaultVatRate !== undefined && userData.defaultVatRate !== null) profileFromApi.defaultVatRate = userData.defaultVatRate;
          if (userData.primaryColor) profileFromApi.primaryColor = userData.primaryColor;
          if (userData.secondaryColor) profileFromApi.secondaryColor = userData.secondaryColor;
          if (userData.industry) profileFromApi.industry = userData.industry;
          if (userData.legalStatus) profileFromApi.legalStatus = userData.legalStatus;
          if (userData.documentTheme) profileFromApi.documentTheme = userData.documentTheme;

          const existing = loadCompanyProfile() || {};
          saveCompanyProfile({
            ...existing,
            ...profileFromApi,
          });

          setCompanyProfile((prev) => ({
            ...prev,
            ...profileFromApi,
          }));
        }
      } catch (error) {
        console.error('Erreur chargement profil entreprise:', error);
      }
    };

    syncFromStorage();
    fetchProfile();

    const handleCompanyProfileUpdated = () => {
      syncFromStorage();
      fetchProfile();
    };

    window.addEventListener('companyProfileUpdated', handleCompanyProfileUpdated);

    const handleStorage = () => {
      syncFromStorage();
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('companyProfileUpdated', handleCompanyProfileUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    onLogout();
    navigate('/login');
  };

  const menuItems = [
    { text: t(language, 'nav.dashboard'), icon: <Dashboard />, path: '/dashboard' },
    { text: t(language, 'nav.myCompany'), icon: <Business />, path: '/company-profile' },
    { text: t(language, 'nav.team'), icon: <Group />, path: '/team', ownerOnly: true },
    { text: t(language, 'nav.clients'), icon: <Business />, path: '/companies' },
    { text: t(language, 'nav.quotes'), icon: <Description />, path: '/quotes' },
    { text: t(language, 'nav.invoices'), icon: <Receipt />, path: '/invoices' },
    { text: t(language, 'nav.expenseNotes'), icon: <LocalAtm />, path: '/expense-notes' },
    { text: t(language, 'nav.supplierArchives'), icon: <Archive />, path: '/external-documents' },
    { text: t(language, 'nav.accounting'), icon: <AccountBalance />, path: '/accounting' },
    { text: t(language, 'nav.accountingReports'), icon: <Assessment />, path: '/accounting-reports' },
    { text: t(language, 'nav.siteStats'), icon: <Assessment />, path: '/admin/stats', adminOnly: true },
    { text: t(language, 'nav.subscriptions'), icon: <CreditCard />, path: '/pricing' },
    { text: t(language, 'nav.settings'), icon: <Settings />, path: '/settings' },
  ];

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.ownerOnly && !isAdmin && storedUserSubscription?.teamRole !== 'OWNER' && storedUserSubscription?.accountId) return false;
    return true;
  });

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1, overflow: 'hidden' }}>
      <BrandHeader>
        <Box sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
          {displayCompanyLogo ? (
            <Box
              component="img"
              src={displayCompanyLogo}
              alt={displayCompanyName}
              sx={{
                maxHeight: 120,
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                display: 'block',
                mx: 'auto',
                objectFit: 'contain',
                objectPosition: 'center',
                filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))'
              }}
            />
          ) : (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#5E564F',
                lineHeight: 1.2,
              }}
            >
              {displayCompanyName}
            </Typography>
          )}
        </Box>
        <Typography 
          variant="caption" 
          sx={{ 
            opacity: 0.9,
            position: 'relative',
            zIndex: 1
          }}
        >
          {displayCompanyName ? t(language, 'header.professionalManagement') : t(language, 'header.configureProfile')}
        </Typography>
      </BrandHeader>
      
      <Divider sx={{ backgroundColor: 'rgba(107, 98, 91, 0.14)' }} />
      
      <List sx={{ flex: 1, minHeight: 0, py: 2, overflowY: 'auto', overflowX: 'hidden',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(107, 98, 91, 0.35)', borderRadius: 8 },
      }}>
                {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ModernListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path ? '#5E564F' : '#6E645D',
                  minWidth: 40,
                  transition: 'all 0.3s ease',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  sx: {
                    color: location.pathname === item.path ? '#5E564F' : '#6E645D',
                    fontWeight: location.pathname === item.path ? 700 : 500,
                    transition: 'all 0.3s ease',
                  }
                }}
              />
            </ModernListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <ModernAppBar position="fixed">
        <Toolbar sx={{ px: { xs: 0.75, md: 2 }, minHeight: { xs: 64, md: 68 }, overflow: { xs: 'hidden', md: 'visible' }, alignItems: 'center' }}>
          <Box
            component="img"
            src={facteioLogo}
            alt="Factéio"
            onClick={() => navigate('/dashboard')}
            sx={{
              height: { xs: 42, md: 54 },
              width: 'auto',
              display: 'block',
              alignSelf: 'center',
              mr: { xs: 0.5, md: 1.2 },
              cursor: 'pointer',
              filter: 'drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.3))'
            }}
          />

          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: { xs: 1, md: 2 }, 
              display: { md: 'none' },
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(197, 164, 109, 0.2)',
                transform: 'scale(1.1)',
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h3" 
            component="div" 
            noWrap
            sx={{ 
              flexGrow: 1,
              display: { xs: 'none', sm: 'block' },
              textAlign: 'center',
              px: 1,
              color: '#ffffff',
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '1.9rem' },
              textShadow: '0px 2px 6px rgba(15, 23, 42, 0.4)',
              letterSpacing: '0.5px'
            }}
          >
            {displayCompanyName}
          </Typography>
          
          <IconButton
            size="large"
            color="inherit"
            sx={{ 
              mr: 1,
              display: { xs: 'none', sm: 'inline-flex' },
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(197, 164, 109, 0.2)',
              }
            }}
          >
            <Badge badgeContent={0} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          
          {/* Badge PRO */}
          {isProPlan && (
            <Chip 
              icon={<Star sx={{ fontSize: '18px !important', color: '#fff !important' }} />}
              label="PRO" 
              size="small"
              sx={{ 
                bgcolor: '#5E564F', 
                color: '#fff', 
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 28,
                mr: { xs: 0.75, sm: 1.25, md: 2 },
                cursor: 'pointer',
                boxShadow: '0px 2px 8px rgba(79, 71, 65, 0.3)',
                '&:hover': {
                  bgcolor: '#746960',
                  boxShadow: '0px 4px 12px rgba(79, 71, 65, 0.4)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease',
              }}
              onClick={() => navigate('/subscription')}
            />
          )}

          {activityMode?.label ? (
            <Chip
              label={isXs ? `Mode ${activityMode.label}` : `Mode ${activityMode.label} activé`}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.18)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 28,
                maxWidth: { xs: 120, sm: 185, md: 240 },
                mr: { xs: 0.75, sm: 1.25, md: 2 },
                border: '1px solid rgba(255, 255, 255, 0.22)',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          ) : null}
          
          {/* Sélecteur de langue */}
          <Tooltip title="Langue / Language / Idioma">
            <Chip
              label={languages[language]?.flag + ' ' + languages[language]?.code}
              size="small"
              onClick={(e) => setLangAnchorEl(e.currentTarget)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.78rem',
                height: 28,
                mr: { xs: 0.5, sm: 1 },
                cursor: 'pointer',
                border: '1px solid rgba(255,255,255,0.22)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              }}
            />
          </Tooltip>
          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={() => setLangAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                borderRadius: 3,
                background: 'linear-gradient(135deg, #FDFBF8 0%, #F2ECE5 100%)',
                border: '1px solid rgba(107, 98, 91, 0.18)',
                boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
                mt: 1,
                minWidth: 160,
              }
            }}
          >
            {Object.entries(languages).map(([code, lang]) => (
              <MenuItem
                key={code}
                selected={language === code}
                onClick={() => { changeLanguage(code); setLangAnchorEl(null); }}
                sx={{ fontWeight: language === code ? 700 : 400 }}
              >
                <Box component="span" sx={{ mr: 1.5, fontSize: '1.2rem' }}>{lang.flag}</Box>
                {lang.label}
              </MenuItem>
            ))}
          </Menu>

          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <GlowingAvatar sx={{ width: 36, height: 36 }}>
              <AccountCircle />
            </GlowingAvatar>
          </IconButton>
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                borderRadius: 3,
                background: 'linear-gradient(135deg, #FDFBF8 0%, #F2ECE5 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(107, 98, 91, 0.18)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
                mt: 1,
              }
            }}
          >
            <MenuItem onClick={() => { handleClose(); handleNavigation('/settings'); }}>
              <Settings sx={{ mr: 1, color: '#64748B' }} />
              {t(language, 'header.settings')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1, color: '#64748B' }} />
              {t(language, 'header.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </ModernAppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <ModernDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              top: '64px',
              height: 'calc(100vh - 64px)',
              overflowY: 'hidden',
            }
          }}
        >
          {drawer}
        </ModernDrawer>
        
        <ModernDrawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              top: '64px',
              height: 'calc(100vh - 64px)',
            }
          }}
          open
        >
          {drawer}
        </ModernDrawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(145deg, #F4F0EB 0%, #ECE6DF 50%, #E4DDD6 100%)',
          position: 'relative',
          
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(107, 98, 91, 0.06) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(197, 164, 109, 0.05) 0%, transparent 50%)',
            opacity: 0.5,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Toolbar />
        <Box sx={{ p: 3, position: 'relative', zIndex: 1, flex: 1, minHeight: 0 }}>
          {children}
        </Box>
        <Box sx={{ position: 'relative', zIndex: 1, mt: 'auto', flexShrink: 0 }}>
          <AppFooter />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;